// @flow
import * as React from 'react';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { withRouter, Route } from 'react-router-dom';
import { withLeaflet } from 'react-leaflet';
import { Box } from 'grommet';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import { injectIntl, IntlShape } from 'react-intl';
import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import ShaftIcon from 'esm-web-pro/components/layout/sidePanel/icons/ShaftIcon';
import PillarIcon from 'esm-web-pro/components/layout/sidePanel/icons/PillarIcon';
import PoPIcon from 'esm-web-pro/components/layout/sidePanel/icons/PoPIcon';
import WallPointIcon from 'esm-web-pro/components/layout/sidePanel/icons/WallPointIcon';
import LineCutIcon from 'esm-web-pro/components/layout/sidePanel/icons/LineCutIcon';
import GisPointIcon from 'esm-web-pro/components/layout/sidePanel/icons/GisPointIcon';
import point11Messages from 'esm-web-pro/messages/points11Messages';
import PillarUndergroundConnectionIcon
    from 'esm-web-pro/components/layout/sidePanel/icons/PillarUndergroundConnectionIcon';
import WallPointUndergroundConnectionIcon
    from 'esm-web-pro/components/layout/sidePanel/icons/WallPointUndergroundConnectionIcon';
import CabinetUndergroundConnectionIcon
    from 'esm-web-pro/components/layout/sidePanel/icons/CabinetUndergroundConnectionIcon';
import CabinetIcon from 'esm-web-pro/components/layout/sidePanel/icons/CabinetIcon';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import ModelAttributes from 'esm-web-pro/containers/pages/network/ModelAttributes';
import UseHighlightPoint from 'esm-web-pro/hooks/useHighlightPoint';
import type { LoadingCallback } from 'esm-web-pro/containers/map/editor/BaseEditor';
import ForkIcon from 'esm-web-pro/components/layout/sidePanel/icons/Fork';
import BottomContainer from 'esm-web-pro/components/layout/sidePanel/BottomContainer';
import NavigateBackButton from 'esm-web-pro/components/maps/NavigateBackButton';
import { POINTS_WITH_CABLE_RESERVE_DISPLAY } from 'esm-web-pro/redux/networks/constants';
import Point11Actions from './Point11Actions';
import Point11Attributes from './Point11Attributes';
import Point11Content from './Point11Content';

type Props = {
    panelId: String,
    object: Object,
    map: Object,
    updateObject: Function,
    editor: BaseEditor,
    history: Object,
    match: Object,
    resource: Object,
    leaflet: LeafletContext,
    intl: IntlShape,
    loadedData: boolean,
    objectLayer: Object,
}

type State = {
    clickedRow: ?string,
};

@injectIntl
class Point11 extends React.Component<Props, State> {
    numberPoint: LoadingCallback;

    state = {
        clickedRow: null,
    };


    constructor(props) {
        super(props);
        this.setNumberPoint();
    }

    componentDidMount() {
        this.focusAndHighlight();
    }

    componentDidUpdate(prevProps): void {
        const { object: { uuid: prevUuid, coordinates: prevCoordinates } } = prevProps;
        const { object: { uuid, coordinates } } = this.props;

        if (prevProps.object !== this.props.object) {
            this.setNumberPoint();
        }

        if (uuid !== prevUuid || coordinates !== prevCoordinates) {
            this.handleView();
        }
    }

    componentWillUnmount() {
        const { editor } = this.props;
        editor.unregisterAllLoadingCallback();
        editor.focus();
    }

    setNumberPoint = () => {
        const { object } = this.props;
        let cableReserves;

        if (POINTS_WITH_CABLE_RESERVE_DISPLAY.includes(object.type)) {
            cableReserves = object.cable_reserves;
        }

        let number = object.sequence;
        if (object.type === 'pop' && object.alias) {
            number = object.alias;
        }
        this.numberPoint = UseHighlightPoint(
            null,
            number,
            object.identity,
            cableReserves,
        );
    }

    focusAndHighlight = () => {
        const { editor, leaflet: { map }, objectLayer } = this.props;

        if (!map) return;
        const coords = this.getPointCoords();
        map.highlightedGeometry = {
            bounds: null, center: [coords[1], coords[0]],
        };
        editor.registerLoadingCallback(this.numberPoint);
        map.panTo([coords[1], coords[0]]);
        // This handles focusing the layer if the component got unmounted and remounted
        // Check if it's not already focused first
        if (!editor.state.focusedLayers.includes(objectLayer)) {
            editor.focus(objectLayer);
        }
        editor.reloadFeatures();
    };

    handleRowClick = (clickedRow) => {
        this.setState({ clickedRow });
    };

    handleView = () => {
        const { editor, leaflet: { map } } = this.props;
        editor.unregisterAllLoadingCallback();
        editor.registerLoadingCallback(this.numberPoint);
        if (map) {
            const coords = this.getPointCoords();
            map.highlightedGeometry = {
                bounds: null, center: [coords[1], coords[0]],
            };
            map.panTo([coords[1], coords[0]]);
            editor.reloadFeatures();
        }
        this.setState({ clickedRow: null });
    };

    getPointCoords = () => {
        const { object: point } = this.props;
        return point.coordinates.coordinates;
    };

    handleEdit = (data: Object) => {
        const { updateObject } = this.props;
        updateObject(data);
    };

    handleAddContent = (data: Object) => {
        const { updateObject } = this.props;
        updateObject(data);
    };

    setIcon = () => {
        const { object: point } = this.props;
        switch (point.type) {
        case 'shaft':
            return <ShaftIcon />;
        case 'pillar':
            if (point.underground_connection) {
                return <PillarUndergroundConnectionIcon />;
            }
            return <PillarIcon />;
        case 'pop':
            return <PoPIcon />;
        case 'wallpoint':
            if (point.underground_connection) {
                return <WallPointUndergroundConnectionIcon />;
            }
            return <WallPointIcon />;
        case 'cabinet':
            if (point.underground_connection) {
                return <CabinetUndergroundConnectionIcon />;
            }
            return <CabinetIcon />;
        case 'linecut':
            return <LineCutIcon />;
        case 'fork':
            return <ForkIcon />;
        default:
            return <GisPointIcon />;
        }
    };

    render() {
        const {
            object: point, map: networkMap, match, resource, editor, history, updateObject,
            intl: { formatMessage }, loadedData, objectLayer, panelId,
        } = this.props;
        const { clickedRow } = this.state;

        let title = point.sequence;
        if (point.type === 'pop' && point.alias) {
            title = point.alias;
        }

        return (
            <SidePane
                id={panelId}
                openInitially
                closeAllOpened
                title={title}
                titleLabel={formatMessage(point11Messages[point.type])}
                titleIcon={this.setIcon()}
                titleOnView={this.handleView}
                actions={<NavigateBackButton panelId={panelId} />}
            >
                <Box height="100%" justify="between">
                    <Box flex>
                        <UpperContainer>
                            <HorizontalLine color="grey" />
                            <Point11Attributes point={point} url={match.url} onView={this.handleView} />
                            <HorizontalLine color="grey" />
                            <Box direction="row">
                                <Point11Actions
                                    point={point}
                                    objectLayer={objectLayer}
                                    map={networkMap}
                                    onEdit={this.handleEdit}
                                    onAddContent={this.handleAddContent}
                                    onView={this.handleView}
                                    resource={resource}
                                    editor={editor}
                                    history={history}
                                    match={match}
                                    updateObject={updateObject}
                                />
                            </Box>
                            <HorizontalLine color="grey" />
                        </UpperContainer>
                        <BottomContainer>
                            <Point11Content
                                point={point}
                                resource={resource}
                                match={match}
                                updateObject={updateObject}
                                map={networkMap}
                                editor={editor}
                                onView={this.handleView}
                                clickedRow={clickedRow}
                                onRowClick={this.handleRowClick}
                                loadedData={loadedData}
                            />
                        </BottomContainer>
                    </Box>

                    <Route
                        path={`${match.path}/model/:modelCode`.replace('//', '/')}
                        render={({ match: _match }) => (
                            <ModelAttributes url={_match.url} />
                        )}
                    />
                </Box>
            </SidePane>
        );
    }
}

export default withRouter(withLeaflet(Point11));
